import { ExportProviderAvailabilityType, NetworkProviderComparisonType, NetworkProviderFilterType, ProviderDropdownTypes } from "@app/exports/enumerations";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { ProjectModule } from "@app/projects/literals";
import { TooltipWrapper } from "@app/shared";
import { QESCheckbox, QESRadioButton, QESSelect } from "@app/shared/qes-standards";
import { RoleRestricted, Roles, UserInfo } from "@app/user";
import classNames from "classnames";
import { filter, some, uniqueId } from "lodash";
import { Component } from "react";

interface SelectProviderFilterProps {
  activeModule: ProjectModule;
  appendNextClosestProviders: boolean;
  isLatestPublished: boolean;
  networkProviderFilterType: NetworkProviderFilterType;
  networkTypes: ProviderNetworkType[];
  user: UserInfo;
  onAppendNextClosestProvidersChange: (appendNextClosest: boolean) => void;
  onNetworkProviderFilterTypeChange: (networkProviderFilterType: NetworkProviderFilterType) => void;
  onProviderComparisonTypeChange: (comparisonType: NetworkProviderComparisonType) => void;
  onProviderFilterTypeChange: (filterType: ProviderNetworkType) => void;
  onProviderAvailabilityTypeChange: (providerAvailablityType: ExportProviderAvailabilityType) => void;
  providerComparisonTypes: NetworkProviderComparisonType[];
  providerFilter: {
    filterType: ProviderNetworkType;
    providerAvailabilityType: ExportProviderAvailabilityType;
  };
  providerIds: number[];
  showChangedProvidersSelection: boolean;
  showAppendNextClosestProviders: boolean;
  showNetNewServicingProviders: boolean;
  showNextClosestProvider: boolean;
  showPatternOfCareProviders?: boolean;
  isFormatHsd?: boolean;
  appendPatternOfCareProviders: boolean;
  onPatternOfCareProvidersChange: (appendPatternOfCare: boolean) => void;
}

export default class SelectProviderFilter extends Component<SelectProviderFilterProps> {
  private adequacyProviderDropdownTypes = [
    { key: ProviderDropdownTypes.Network, value: "Network Servicing Providers" },
    { key: ProviderDropdownTypes.Prospect, value: "Prospect Servicing Providers" },
    { key: ProviderDropdownTypes.Market, value: "Market Servicing Providers" },
    { key: ProviderDropdownTypes.NextClosestProviders, value: "Next Closest Providers" },
    { key: ProviderDropdownTypes.NetNew, value: "Net New Servicing Providers" }
  ];

  private accuracyProviderDropdownTypes = [
    { key: ProviderDropdownTypes.Network, value: "Network Providers" }
  ];

  private providerAvailabilityTypes = [
    { key: ExportProviderAvailabilityType.All, value: "All Providers" },
    { key: ExportProviderAvailabilityType.Available, value: "Available Providers" },
    { key: ExportProviderAvailabilityType.Changed, value: "Changed Providers" },
    { key: ExportProviderAvailabilityType.Excluded, value: "Excluded Providers" },
    { key: ExportProviderAvailabilityType.Targeted, value: "Targeted Providers" },
  ];

  private providerFilterTypes = [
    { key: NetworkProviderFilterType.Available, value: "All Providers" },
    { key: NetworkProviderFilterType.Unterminated, value: "Network Minus Terminated Providers" },
    { key: NetworkProviderFilterType.Terminated, value: "Terminated Providers" },
  ];

  private providerComparisonTypes = [
    { key: NetworkProviderComparisonType.Added, value: "Added Providers" },
    { key: NetworkProviderComparisonType.Matched, value: "Matched Providers" },
    { key: NetworkProviderComparisonType.Removed, value: "Removed Providers" },
  ];

  public render() {
    const hasProviderIds = this.props.providerIds.length > 0;

    let filteredProviderTypes = Array.from(filter(this.props.activeModule === "Adequacy" ? this.adequacyProviderDropdownTypes : this.accuracyProviderDropdownTypes, ({ key }) => some(this.props.networkTypes, (nt) => nt.valueOf() === key.valueOf())));
    if (this.props.showNextClosestProvider && this.props.activeModule === "Adequacy") { filteredProviderTypes = filteredProviderTypes.concat({ key: ProviderDropdownTypes.NextClosestProviders, value: "Next Closest Providers" }); }
    if (!filteredProviderTypes.some(p => p.key === ProviderDropdownTypes.NetNew) &&
      this.props.user.roles.includes(Roles.VOLUME_TARGET) &&
      this.props.showNetNewServicingProviders &&
      this.props.activeModule === "Adequacy" &&
      this.props.networkTypes.length > 1) {
      filteredProviderTypes = filteredProviderTypes.concat({ key: ProviderDropdownTypes.NetNew, value: "Net New Servicing Providers" });
    }
    return (
      <>
        <p className="lead mb0 font-weight-bold">Provider Filter{hasProviderIds && <span>: Selected</span>}</p>
        <p>Please select the providers to include:</p>
        <div className="form-group row">
          <div className="col-lg-12 ptb0">
            <QESSelect
              label="Provider Type"
              hideLabel={true}
              testId="select-provider-filter"
              disabled={hasProviderIds || this.props.providerFilter.providerAvailabilityType === ExportProviderAvailabilityType.Changed}
              className="form-control"
              value={this.props.providerFilter.filterType}
              onChange={(e) => this.props.onProviderFilterTypeChange(e.target.value as ProviderNetworkType)}
            >
              {filteredProviderTypes.map(({ key, value }) => (
                <option key={key} value={key}>{value}</option>
              ))}
            </QESSelect>
          </div>
        </div>
        {this.renderAppendNextClosestProviders()}
        {this.renderAppendPatternOfCareProviders()}
        {this.renderNetworkProviderFilterType(hasProviderIds)}
        {this.renderProviderAvailabilitySelection(hasProviderIds)}
        {this.renderProviderComparisonTypes()}
      </>
    );
  }

  private renderNetworkProviderFilterType(hasProviderIds: boolean) {
    return this.props.providerFilter.filterType === ProviderNetworkType.Network && this.props.providerFilter.providerAvailabilityType !== ExportProviderAvailabilityType.Changed && (
      <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN, Roles.TERM_PROVIDER_VIEWER]}>
        <div className="form-group row">
          <div className="col-lg-12 ptb0 mt20 qes-radio-group__container">
            <p className="mb-1 col-form-label text-left">Filter</p>
            {this.providerFilterTypes.map(({ key, value }) => {

              return !this.props.isLatestPublished && [NetworkProviderFilterType.Terminated, NetworkProviderFilterType.Unterminated].includes(key)
                ? (
                  <div key={key.toString()} className="qes-radio-group__radio-item">
                    <TooltipWrapper text={`${value} is only available on the latest project run.`}>
                      <QESRadioButton
                        label={value}
                        disabled={true}
                        value={key.toString()}
                        checked={this.props.networkProviderFilterType === key}
                        onChange={(e) => e.target.checked ? this.props.onNetworkProviderFilterTypeChange(key) : {}}
                        testId={`select-provider-filter__${key}`}
                        name="network-provider-filter-type"
                      />
                    </TooltipWrapper>
                  </div>
                )
                : (
                  <div key={key.toString()} className="qes-radio-group__radio-item">
                    <QESRadioButton
                      label={value}
                      disabled={hasProviderIds}
                      value={key.toString()}
                      checked={this.props.networkProviderFilterType === key}
                      onChange={(e) => e.target.checked ? this.props.onNetworkProviderFilterTypeChange(key) : {}}
                      testId={`select-provider-filter__${key}`}
                      name="network-provider-filter-type"
                    />
                  </div>
                );
            })}
          </div>
        </div>
      </RoleRestricted>
    );
  }

  private renderAppendNextClosestProviders() {
    const nextClosestCheckboxId = uniqueId("append-next-closest");
    return this.props.showAppendNextClosestProviders && this.props.providerFilter.filterType === ProviderNetworkType.Network && (
      <QESCheckbox
        containerClassName="form-check mt-0"
        label="Append Next Closest Network Providers"
        id={nextClosestCheckboxId}
        value="active"
        checked={this.props.appendNextClosestProviders}
        onChange={(e) => this.props.onAppendNextClosestProvidersChange(e.target.checked)}
        testId="select-provider-filter__append-ncp"
      />
    );
  }

  private renderAppendPatternOfCareProviders() {
    const isDisabled = this.props.providerFilter.filterType !== ProviderNetworkType.Network || !this.props.isFormatHsd;
    return this.props.showPatternOfCareProviders
      && (
        <TooltipWrapper text={isDisabled ? "Select Network Providers in the Provider Filter and MA HSD or MMP HSD Format to Append Pattern of Care Providers" : ""}>
          <QESCheckbox
            containerClassName="form-check mt-0"
            label="Append Pattern of Care Providers"
            checked={this.props.appendPatternOfCareProviders}
            onChange={(e) => this.props.onPatternOfCareProvidersChange(e.target.checked)}
            testId="select-provider-filter__append-pattern-of-care"
            disabled={isDisabled}
          />
        </TooltipWrapper>
      );
  }

  private renderProviderComparisonTypes() {
    return this.props.providerFilter.filterType === ProviderNetworkType.Network && this.props.showChangedProvidersSelection && (
      <div className="form-group row">
        <div className="col-lg-12 ptb0 mt20">
          <p className="mb-1 col-lg-4 col-form-label text-left">Filter</p>
          {this.providerComparisonTypes.map(({ key, value }) => {
            const inputId = uniqueId(key);

            return (
              <div key={key.toString()} className={classNames("col-xl-10 col-lg-9 mt0")}>
                <QESCheckbox
                  containerClassName="form-check"
                  className="form-check-input mt-0"
                  label={value}
                  id={inputId}
                  value={key.toString()}
                  checked={this.props.providerComparisonTypes.indexOf(key) > -1}
                  onChange={(e) => this.props.onProviderComparisonTypeChange(e.target.value as NetworkProviderComparisonType)}
                  testId={`select-provider-filter__${key}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  private renderProviderAvailabilitySelection(hasProviderIds: boolean) {
    return this.props.providerFilter.filterType !== ProviderNetworkType.Network && this.props.providerFilter.filterType.toString() !== ProviderDropdownTypes.NextClosestProviders.toString() && (
      <>
        <div className="form-group row">
          <div className="col-lg-12 ptb0 mt20 qes-radio-group__container">
            <p className="mb-1 col-form-label text-left">Filter</p>
            {this.providerAvailabilityTypes.filter(({ key }) => {
              if (key === ExportProviderAvailabilityType.Changed) {
                return this.props.showChangedProvidersSelection;
              }
              else if ([ExportProviderAvailabilityType.Excluded, ExportProviderAvailabilityType.Available].includes(key)) {
                return this.props.providerFilter.filterType !== ProviderNetworkType.NetNew;
              }
              else {
                return true;
              }
            }).map(({ key, value }) => {

              return !this.props.isLatestPublished && [ExportProviderAvailabilityType.Excluded, ExportProviderAvailabilityType.Targeted].includes(key)
                ? (
                  <div key={key.toString()} className="qes-radio-group__radio-item">
                    <TooltipWrapper text={`${value} is only available on the latest project run.`}>
                      <QESRadioButton
                        label={value}
                        disabled={true}
                        value={key.toString()}
                        checked={this.props.providerFilter.providerAvailabilityType === key}
                        onChange={(e) => e.target.checked ? this.props.onProviderAvailabilityTypeChange(key) : {}}
                        testId={`select-provider-filter__${key}`}
                        name="select-providers-availability"
                      />
                    </TooltipWrapper>
                  </div>
                )
                : (
                  <div key={key.toString()} className="qes-radio-group__radio-item">
                    <QESRadioButton
                      label={value}
                      disabled={hasProviderIds || this.props.providerFilter.providerAvailabilityType === ExportProviderAvailabilityType.Changed}
                      value={key.toString()}
                      checked={this.props.providerFilter.providerAvailabilityType === key}
                      onChange={(e) => e.target.checked ? this.props.onProviderAvailabilityTypeChange(key) : {}}
                      testId={`select-provider-filter__${key}`}
                      name="select-providers-availability"
                    />
                  </div>
                );
            })}
          </div>
        </div>
      </>
    );
  }
}